import './assets/css/pages/page-template-quote.scss'

/**
 * Quote Form Chips
 *
 * Progressively enhances the products `<select multiple>` on the single-page
 * quote form (Gravity Form id 4) into a dropdown trigger + filterable panel +
 * chip rail. Each selected chip carries a thumbnail and a per-product
 * quantity input. The native select stays in the DOM for submission; the
 * quantities are serialized into the hidden "Product quantities" field
 * (`.aa-quote-quantities`) as a readable one-line-per-product breakdown so
 * they show up cleanly in entries and the admin notification.
 *
 * Backend-management safe: the products field is found by its
 * `aa-quote-products` cssClass (falling back to the lone multiselect), and the
 * quantities field by `aa-quote-quantities` — not by hardcoded field IDs, so
 * reordering/relabelling fields in the GF admin won't break it. If this script
 * fails to load, the native multiselect still submits products and the form
 * keeps working (quantities are simply absent).
 */

type ProductOption = {
  value: string
  label: string
  image: string
}

const QUOTE_FORM_ID = '4'

/** product title -> thumbnail URL, emitted by templates/quote.php. */
function readImageMap(): Record<string, string> {
  const el = document.getElementById('aa-quote-products')
  if (!el) return {}
  try {
    const data = JSON.parse(el.textContent ?? '{}')
    return data && typeof data === 'object' ? (data as Record<string, string>) : {}
  } catch {
    return {}
  }
}

function boot(): void {
  const wrap = document.querySelector<HTMLElement>(`[data-quote-form="${QUOTE_FORM_ID}"]`)
  if (!wrap) return
  // Prefer the `aa-quote-products` field (cssClass set in the Form 4 schema);
  // fall back to the lone multiselect so a relabelled field still enhances.
  const select =
    wrap.querySelector<HTMLSelectElement>('.aa-quote-products select[multiple]') ??
    wrap.querySelector<HTMLSelectElement>('select[multiple]')
  if (!select) return
  enhance(wrap, select)
}

function enhance(wrap: HTMLElement, select: HTMLSelectElement): void {
  // Hide the native select but keep it in the accessibility tree so the
  // underlying control still works if event binding fails.
  select.classList.add('aa-chip-select__native')
  select.setAttribute('tabindex', '-1')

  const images = readImageMap()
  // Hidden GF field that captures the readable per-product breakdown.
  const quantitiesField = wrap.querySelector<HTMLTextAreaElement | HTMLInputElement>(
    '.aa-quote-quantities textarea, .aa-quote-quantities input'
  )

  const fieldWrap = select.closest('.gfield') as HTMLElement | null
  const host = document.createElement('div')
  host.className = 'aa-chip-select'
  host.setAttribute('data-aa-chip-select', '')
  ;(select.parentElement ?? fieldWrap ?? wrap).appendChild(host)

  // Authoritative option list from the native select's <option>s (choices are
  // populated server-side by Quote_Tool::populate_industry_choices()).
  const options: ProductOption[] = Array.from(select.options).map(o => {
    const value = o.value
    return {
      value,
      label: o.textContent?.trim() ?? value,
      image: images[value] ?? '',
    }
  })

  // value -> entered quantity, kept across chip re-renders.
  const qtyMap = new Map<string, string>()

  const chipRail = document.createElement('ul')
  chipRail.className = 'aa-chip-select__chips'
  chipRail.setAttribute('aria-live', 'polite')

  const trigger = document.createElement('button')
  trigger.type = 'button'
  trigger.className = 'aa-chip-select__trigger'
  trigger.setAttribute('aria-haspopup', 'listbox')
  trigger.setAttribute('aria-expanded', 'false')

  const panel = document.createElement('div')
  panel.className = 'aa-chip-select__panel'
  panel.setAttribute('role', 'listbox')
  panel.setAttribute('aria-multiselectable', 'true')
  panel.hidden = true

  const search = document.createElement('input')
  search.type = 'search'
  search.className = 'aa-chip-select__search'
  search.placeholder = 'Filter products…'
  search.setAttribute('aria-label', 'Filter products')

  const list = document.createElement('ul')
  list.className = 'aa-chip-select__list'

  const itemNodes = new Map<string, HTMLButtonElement>()

  function makeThumb(opt: ProductOption, cls: string): HTMLImageElement | null {
    if (!opt.image) return null
    const img = document.createElement('img')
    img.className = cls
    img.src = opt.image
    img.alt = ''
    img.loading = 'lazy'
    img.setAttribute('aria-hidden', 'true')
    return img
  }

  // Serialize the selected products + their quantities into the hidden GF
  // field, one readable line per product.
  function writeQuantities(): void {
    if (!quantitiesField) return
    const lines = Array.from(select.selectedOptions).map(o => {
      const label = options.find(x => x.value === o.value)?.label ?? o.value
      const qty = (qtyMap.get(o.value) ?? '').trim()
      return qty !== '' ? `${label} — ${qty}` : `${label} — (no quantity specified)`
    })
    quantitiesField.value = lines.join('\n')
    quantitiesField.dispatchEvent(new Event('change', { bubbles: true }))
  }

  function syncFromNative(): void {
    const selected = new Set(Array.from(select.selectedOptions).map(o => o.value))
    itemNodes.forEach((btn, val) => {
      const isSelected = selected.has(val)
      btn.setAttribute('aria-selected', isSelected ? 'true' : 'false')
      btn.classList.toggle('is-selected', isSelected)
    })
    chipRail.replaceChildren()
    options.filter(o => selected.has(o.value)).forEach(o => chipRail.appendChild(buildChip(o)))
    trigger.textContent = 'Add product'
    writeQuantities()
  }

  function setSelected(value: string, on: boolean): void {
    for (const opt of select.options) {
      if (opt.value === value) {
        opt.selected = on
        break
      }
    }
    if (!on) qtyMap.delete(value)
    select.dispatchEvent(new Event('change', { bubbles: true }))
    syncFromNative()
  }

  function buildChip(opt: ProductOption): HTMLLIElement {
    const li = document.createElement('li')
    li.className = 'aa-chip-select__chip'

    const img = makeThumb(opt, 'aa-chip-select__chip-thumb')
    if (img) li.appendChild(img)

    const label = document.createElement('span')
    label.className = 'aa-chip-select__chip-label'
    label.textContent = opt.label
    li.appendChild(label)

    const qty = document.createElement('input')
    qty.type = 'number'
    qty.min = '0'
    qty.inputMode = 'decimal'
    qty.className = 'aa-chip-select__chip-qty'
    qty.placeholder = 'tons / yards'
    qty.setAttribute('aria-label', `Quantity for ${opt.label} (tons or yards)`)
    qty.value = qtyMap.get(opt.value) ?? ''
    qty.addEventListener('input', () => {
      qtyMap.set(opt.value, qty.value)
      writeQuantities()
    })
    li.appendChild(qty)

    const remove = document.createElement('button')
    remove.type = 'button'
    remove.className = 'aa-chip-select__chip-remove'
    remove.setAttribute('aria-label', `Remove ${opt.label}`)
    remove.textContent = '×'
    remove.addEventListener('click', e => {
      e.stopPropagation()
      setSelected(opt.value, false)
    })
    li.appendChild(remove)
    return li
  }

  function buildListItem(opt: ProductOption): HTMLLIElement {
    const li = document.createElement('li')
    li.className = 'aa-chip-select__item'
    const btn = document.createElement('button')
    btn.type = 'button'
    btn.className = 'aa-chip-select__item-button'
    btn.setAttribute('role', 'option')
    btn.setAttribute('aria-selected', 'false')
    btn.dataset.value = opt.value

    const img = makeThumb(opt, 'aa-chip-select__item-thumb')
    if (img) btn.appendChild(img)
    const label = document.createElement('span')
    label.className = 'aa-chip-select__item-label'
    label.textContent = opt.label
    btn.appendChild(label)

    btn.addEventListener('click', () => {
      const isOn = btn.getAttribute('aria-selected') === 'true'
      setSelected(opt.value, !isOn)
    })
    itemNodes.set(opt.value, btn)
    li.appendChild(btn)
    return li
  }

  options.forEach(o => list.appendChild(buildListItem(o)))

  function openPanel(): void {
    panel.hidden = false
    trigger.setAttribute('aria-expanded', 'true')
    search.focus()
  }
  function closePanel(): void {
    panel.hidden = true
    trigger.setAttribute('aria-expanded', 'false')
  }

  trigger.addEventListener('click', () => {
    if (panel.hidden) openPanel()
    else closePanel()
  })

  search.addEventListener('input', () => {
    const q = search.value.trim().toLowerCase()
    list.querySelectorAll<HTMLLIElement>('.aa-chip-select__item').forEach(li => {
      const btn = li.querySelector<HTMLButtonElement>('.aa-chip-select__item-button')
      const match = !q || (btn?.textContent?.toLowerCase().includes(q) ?? false)
      li.style.display = match ? '' : 'none'
    })
  })

  // Close on outside click + Esc
  document.addEventListener('click', e => {
    if (panel.hidden) return
    if (host.contains(e.target as Node)) return
    closePanel()
  })
  panel.addEventListener('keydown', e => {
    if (e.key === 'Escape') {
      closePanel()
      trigger.focus()
    }
  })

  panel.appendChild(search)
  panel.appendChild(list)
  host.appendChild(trigger)
  host.appendChild(panel)
  host.appendChild(chipRail)

  // Keep in sync if anything else mutates the native select (autofill, prefill).
  select.addEventListener('change', syncFromNative)

  syncFromNative()
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', boot)
} else {
  boot()
}
